import type { RouteLocationNormalized, RouteRecordRaw, Router } from 'vue-router'
import { useLDFlag } from 'launchdarkly-vue-client-sdk'
import { AdminStatesConstant } from '@/branchmanager/app/constant/AdminStatesConstant'
import { readOptionalModuleExport } from '@/common/utils/ModuleUtil'

const MEMBERSHIP_ELIGIBILITY_FLAG_KEY = 'oa-membership-eligibility'
const MEMBERSHIP_ELIGIBILITY_FALLBACK_ROUTE = { path: '/' } as const

type MembershipEligibilityRemoteGetRoutes = () => RouteRecordRaw[]

interface MembershipEligibilityRemoteRouterModule {
  getRoutes?: MembershipEligibilityRemoteGetRoutes
  default?: MembershipEligibilityRemoteGetRoutes | {
    getRoutes?: MembershipEligibilityRemoteGetRoutes
  }
}

class MembershipEligibilityGuardFactory {
  private static remoteRoutesLoaded = false

  static create(getRouter: () => Router) {
    return async (to: RouteLocationNormalized) => {
      if (!MembershipEligibilityGuardFactory.isMembershipEligibilityEnabled()) {
        return MEMBERSHIP_ELIGIBILITY_FALLBACK_ROUTE
      }

      if (MembershipEligibilityGuardFactory.remoteRoutesLoaded) {
        return true
      }

      try {
        await MembershipEligibilityGuardFactory.loadMembershipEligibilityRemoteRoutes(getRouter())
        return { ...to, replace: true }
      } catch (error) {
        console.error('Failed to load membership eligibility remote routes', {
          error,
          routeName: to.name,
          fullPath: to.fullPath,
          flagEnabled: MembershipEligibilityGuardFactory.isMembershipEligibilityEnabled(),
          remoteRoutesLoaded: MembershipEligibilityGuardFactory.remoteRoutesLoaded
        })

        return MEMBERSHIP_ELIGIBILITY_FALLBACK_ROUTE
      }
    }
  }

  private static isMembershipEligibilityEnabled(): boolean {
    return useLDFlag(MEMBERSHIP_ELIGIBILITY_FLAG_KEY, false).value
  }

  private static async loadMembershipEligibilityRemoteRoutes(router: Router): Promise<void> {
    const remoteRouterModule = await import('membership-eligibility-configuration/router') as MembershipEligibilityRemoteRouterModule
    const getRoutes = MembershipEligibilityGuardFactory.resolveMembershipEligibilityRemoteGetRoutes(remoteRouterModule)
    const routes = getRoutes()

    if (!Array.isArray(routes)) {
      throw new TypeError('membership-eligibility-configuration/router getRoutes did not return an array')
    }

    routes.forEach((route) => {
      if (route.name && router.hasRoute(route.name)) {
        return
      }

      router.addRoute(AdminStatesConstant.CU_MEMBERSHIP_ELEGIBILITY.STATE, route)
    })

    MembershipEligibilityGuardFactory.remoteRoutesLoaded = true
  }

  private static resolveMembershipEligibilityRemoteGetRoutes(
    remoteRouterModule: MembershipEligibilityRemoteRouterModule
  ): MembershipEligibilityRemoteGetRoutes {
    const moduleGetRoutes = readOptionalModuleExport<MembershipEligibilityRemoteGetRoutes>(
      remoteRouterModule,
      'getRoutes'
    )
    const moduleDefault = readOptionalModuleExport<MembershipEligibilityRemoteRouterModule['default']>(
      remoteRouterModule,
      'default'
    )
    const defaultGetRoutes =
      typeof moduleDefault === 'function'
        ? undefined
        : readOptionalModuleExport<MembershipEligibilityRemoteGetRoutes>(
          moduleDefault,
          'getRoutes'
        )

    const resolved =
      moduleGetRoutes
      ?? defaultGetRoutes
      ?? (typeof moduleDefault === 'function' ? moduleDefault : undefined)

    if (typeof resolved !== 'function') {
      throw new TypeError('membership-eligibility-configuration/router did not provide a getRoutes function')
    }

    return resolved
  }
}

export default MembershipEligibilityGuardFactory



